import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import VideoCard from '../molecules/VideoCard';
import { Video } from '../../types/video';

interface VideoFeedProps {
  videos: Video[];
  onLike: (id: string) => void;
  onComment: (id: string) => void;
  onShare: (id: string) => void;
  onSave: (id: string) => void;
}

const FeedContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 100vh;
  }
`;

const FeedScroller = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 100vh;
  }
`;

const VideoItem = styled.div`
  width: 100%;
  height: 100%;
  scroll-snap-align: start;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 100vh;
  }
`;

const VideoFeed: React.FC<VideoFeedProps> = ({
  videos,
  onLike,
  onComment,
  onShare,
  onSave
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Handle scroll to determine current video
  const handleScroll = () => {
    if (scrollerRef.current) {
      const scrollTop = scrollerRef.current.scrollTop;
      const itemHeight = scrollerRef.current.clientHeight;
      const index = Math.round(scrollTop / itemHeight);
      
      if (index !== currentIndex) {
        setCurrentIndex(index);
      }
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.addEventListener('scroll', handleScroll);
      return () => scroller.removeEventListener('scroll', handleScroll);
    }
  }, [currentIndex]);

  return (
    <FeedContainer>
      <FeedScroller ref={scrollerRef}>
        {videos.map((video, index) => (
          <VideoItem key={video.id}>
            <VideoCard
              id={video.id}
              videoUrl={video.videoUrl}
              thumbnailUrl={video.thumbnailUrl}
              title={video.title}
              creator={video.creator}
              likes={video.likes}
              comments={video.comments}
              location={video.location}
              tags={video.tags}
              isLiked={video.isLiked}
              onLike={onLike}
              onComment={onComment}
              onShare={onShare}
              onSave={onSave}
            />
          </VideoItem>
        ))}
      </FeedScroller>
    </FeedContainer>
  );
};

export default VideoFeed;
